import React, { useId, useState } from "react"

import { Badge, Button, Card, CardContent, Input } from "@coval/ui"
import { ChevronDown, ChevronUp, Copy, HelpCircle, Info } from "lucide-react"

interface MatchPathInputProps {
  value: string
  onChange: (value: string) => void
  expectedBodyValue?: string
}

const TEMPLATE_VAR_EXAMPLE = "orders[id={{test_case.order_id}}]"

const MATCH_PATH_EXAMPLES = [
  {
    title: "Simple key",
    value: "status",
    description: "Extract the 'status' field from the root",
    responseExample: '{"status": "success", "data": {...}}',
  },
  {
    title: "Nested key",
    value: "data.balance",
    description: "Extract balance from nested data object",
    responseExample: '{"data": {"balance": 100.50}}',
  },
  {
    title: "Array index",
    value: "data.accounts[0].balance",
    description: "Extract balance from first account in array",
    responseExample:
      '{"data": {"accounts": [{"balance": 100}, {"balance": 200}]}}',
  },
  {
    title: "Array filter (single)",
    value: "users[role=admin].email",
    description: "Find user with specific role, then get email",
    responseExample:
      '{"users": [{"role": "admin", "email": "admin@example.com"}]}',
  },
  {
    title: "Array filter (multiple)",
    value: "products[category=electronics,brand=Apple]",
    description: "Find product matching multiple criteria",
    responseExample:
      '{"products": [{"category": "electronics", "brand": "Apple", "price": 999}]}',
  },
  {
    title: "With template variable",
    value: "orders[orderId={{test_case.order_id}}].status",
    description: "Use template variable in filter",
    responseExample: '{"orders": [{"orderId": "12345", "status": "shipped"}]}',
  },
]

export const MatchPathInput: React.FC<MatchPathInputProps> = ({
  value,
  onChange,
  expectedBodyValue,
}) => {
  const inputId = useId()
  const [showHelp, setShowHelp] = useState(false)
  const [showExamples, setShowExamples] = useState(false)

  const handleExampleClick = (exampleValue: string) => {
    onChange(exampleValue)
  }

  const hasMatchPath = value.trim().length > 0
  const hasExpectedBody =
    expectedBodyValue && expectedBodyValue.trim().length > 0

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-1 flex-1">
          <label htmlFor={inputId} className="text-sm font-medium">
            Match Path (Optional)
          </label>
          <Input
            id={inputId}
            placeholder="data.balance or data.accounts[0].balance"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="font-mono text-sm"
          />
          <p className="text-xs text-coval-text-muted">
            {hasMatchPath
              ? "Extracting specific field from response"
              : "Leave empty to compare entire response body"}
          </p>
        </div>
        <Button
          icon
          variant="ghost"
          onClick={() => setShowHelp(!showHelp)}
          className="self-start mt-6"
          tooltip="Toggle help"
        >
          <HelpCircle className="w-5 h-5 text-coval-text-muted" />
        </Button>
      </div>

      {!hasMatchPath && hasExpectedBody && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">No Match Path specified</p>
            <p>
              The entire response body will be compared to your Expected Body.
              If the response is large, consider using a Match Path to extract
              only the field you need to compare.
            </p>
          </div>
        </div>
      )}

      {hasMatchPath && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm text-amber-700 dark:text-amber-300">
            <p className="font-medium mb-1">Match Path active</p>
            <p>
              Only the value at{" "}
              <code className="font-mono bg-amber-100 dark:bg-amber-900/50 px-1 rounded">
                {value}
              </code>{" "}
              will be extracted and compared to your Expected Body. Make sure
              your Expected Body matches the type and structure of the extracted
              value.
            </p>
          </div>
        </div>
      )}

      {showHelp && (
        <Card className="mt-2 border border-coval-border">
          <CardContent className="flex flex-col gap-4 pt-6">
            <div>
              <h4 className="font-semibold text-sm mb-2">
                What is Match Path?
              </h4>
              <p className="text-sm text-coval-text-muted mb-3">
                Match Path lets you extract a specific field from the API
                response before comparing it to your Expected Body. This is
                useful when the response contains many fields, but you only care
                about one.
              </p>
              <div className="space-y-2">
                <div className="p-2 bg-surface-1 rounded-lg">
                  <p className="text-xs font-medium mb-1">
                    When to use Match Path:
                  </p>
                  <ul className="text-xs text-coval-text-muted list-disc list-inside space-y-1">
                    <li>Response has many fields, you only need one</li>
                    <li>You need to extract a nested value</li>
                    <li>You need to find an item in an array by criteria</li>
                  </ul>
                </div>
                <div className="p-2 bg-surface-1 rounded-lg">
                  <p className="text-xs font-medium mb-1">
                    When to skip Match Path:
                  </p>
                  <ul className="text-xs text-coval-text-muted list-disc list-inside space-y-1">
                    <li>You want to compare the entire response</li>
                    <li>
                      The response is simple (single value or small object)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => setShowExamples(!showExamples)}
                className="flex items-center gap-2 text-sm font-medium text-coval-info-text hover:underline transition-colors w-full"
              >
                {showExamples ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
                <span>Syntax Examples</span>
              </button>
              {showExamples && (
                <div className="mt-2 space-y-3">
                  {MATCH_PATH_EXAMPLES.map((example, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-surface-1 rounded-lg text-sm border border-coval-border"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge>{example.title}</Badge>
                          </div>
                          <code className="text-coval-info-text font-mono text-xs block mb-1 break-all">
                            {example.value}
                          </code>
                          <p className="text-coval-text-muted text-xs">
                            {example.description}
                          </p>
                        </div>
                        <Button
                          icon
                          variant="ghost"
                          onClick={() => handleExampleClick(example.value)}
                          className="shrink-0"
                          tooltip="Copy match path"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="mt-2 pt-2 border-t border-coval-border">
                        <p className="text-xs text-coval-text-muted mb-1">
                          Example response:
                        </p>
                        <code className="text-xs font-mono text-coval-text-muted break-all">
                          {example.responseExample}
                        </code>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h5 className="font-semibold text-sm mb-2">Syntax Reference</h5>
              <div className="space-y-2 text-xs">
                <div>
                  <strong>Simple key:</strong>{" "}
                  <code className="font-mono">status</code>
                </div>
                <div>
                  <strong>Nested:</strong>{" "}
                  <code className="font-mono">data.balance</code>
                </div>
                <div>
                  <strong>Array index:</strong>{" "}
                  <code className="font-mono">items[0].name</code>
                </div>
                <div>
                  <strong>Array filter:</strong>{" "}
                  <code className="font-mono">
                    products[category=electronics,status=active]
                  </code>
                </div>
                <div>
                  <strong>Template variables:</strong>{" "}
                  <code className="font-mono">{TEMPLATE_VAR_EXAMPLE}</code>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
